import React, { FunctionComponent, useCallback, useMemo } from "react";
import DefaultMap from "@alpaca-travel/react-mapbox-gl/map";
import { MapboxEvent } from "mapbox-gl";
import useMapStyle from "./use-map-style";
import StoreLocations from "./store-locations";
import AlpacaAttributionControl from "@alpaca-travel/react-mapbox-gl/alpaca-attribution-control";
import MapEvent from "@alpaca-travel/react-mapbox-gl/map-event";
import useRefinementEffects from "@/use-refinement-effects";
import useMapboxAccessToken from "@/use-mapbox-access-token";
import useTargetStore from "@/use-target-store";
import useTargetApply from "./use-target-apply";
import Interactions from "./interactions";
import MapNavigationControl from "@/@alpaca-travel/react-mapbox-gl/map-navigation-control";
import MapScaleControl from "@/@alpaca-travel/react-mapbox-gl/map-scale-control";

const Map: FunctionComponent<MapProps> = ({ disableInteractions }) => {
  useMapStyle();

  const { setBounds } = useRefinementEffects();
  const { accessToken } = useMapboxAccessToken();

  const shouldUseCooperative = useMemo(() => {
    return "ontouchstart" in window || "msMaxTouchPoints" in navigator;
  }, []);

  const { viewport } = useTargetStore();

  useTargetApply();

  const onMoveHandler = useCallback(
    (event: MapboxEvent) => {
      const bounds = event.target.getBounds();
      const w = bounds.getWest();
      const s = bounds.getSouth();
      const e = bounds.getEast();
      const n = bounds.getNorth();

      setBounds([
        [w, s],
        [e, n],
      ]);
    },
    [setBounds]
  );

  return (
    <>
      <DefaultMap
        id="alpacaAmpolMap"
        className="tw-absolute tw-top-0 tw-left-0 tw-w-full tw-h-full tw-bg-sky-500"
        accessToken={accessToken}
        cooperativeGestures={shouldUseCooperative}
        bounds={viewport}
        fitBoundsOptions={{ padding: 10 }}
      >
        <AlpacaAttributionControl position="bottom-left" />
        <MapNavigationControl />
        <MapScaleControl position="bottom-right" />

        <MapEvent event="moveend" listener={onMoveHandler} />

        {disableInteractions === false && (
          <>
            <Interactions />
            <StoreLocations />
          </>
        )}
      </DefaultMap>
    </>
  );
};

export type MapProps = {
  disableInteractions: boolean;
};

export default React.memo(Map);
